import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { api, apiErrorMessage } from "@/lib/api";

// Konvensi Python weekday(): 0=Senin ... 6=Minggu
const HARI_LIST = [
  { v: 0, label: "Senin", short: "Sen" },
  { v: 1, label: "Selasa", short: "Sel" },
  { v: 2, label: "Rabu", short: "Rab" },
  { v: 3, label: "Kamis", short: "Kam" },
  { v: 4, label: "Jumat", short: "Jum" },
  { v: 5, label: "Sabtu", short: "Sab" },
  { v: 6, label: "Minggu", short: "Min" },
];
const DEFAULT_HARI_KERJA = [0, 1, 2, 3, 4];

function empty() {
  return {
    nama: "",
    jam_masuk: "08:00",
    jam_pulang: "17:00",
    toleransi_menit: 15,
    hari_kerja: [...DEFAULT_HARI_KERJA],
  };
}

function formatHariKerja(hk) {
  const list = Array.isArray(hk) && hk.length ? hk : DEFAULT_HARI_KERJA;
  if (list.length === 5 && [0, 1, 2, 3, 4].every((v) => list.includes(v))) return "Senin – Jumat";
  if (list.length === 6 && [0, 1, 2, 3, 4, 5].every((v) => list.includes(v))) return "Senin – Sabtu";
  if (list.length === 7) return "Setiap Hari";
  return list.sort((a, b) => a - b).map((v) => HARI_LIST.find((h) => h.v === v)?.short).join(", ");
}

export default function AdminJadwal() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/master/jadwal"); setRows(data); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.nama.trim()) {
      toast.error("Nama jadwal wajib diisi");
      return;
    }
    if (!Array.isArray(form.hari_kerja) || form.hari_kerja.length === 0) {
      toast.error("Pilih minimal satu hari kerja");
      return;
    }
    const payload = {
      ...form,
      toleransi_menit: parseInt(form.toleransi_menit, 10),
      hari_kerja: [...form.hari_kerja].sort((a, b) => a - b),
    };
    try {
      if (editing) await api.put(`/master/jadwal/${editing.id}`, payload);
      else await api.post("/master/jadwal", payload);
      toast.success("Berhasil disimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }
  function toggleHari(v) {
    setForm((s) => {
      const set = new Set(s.hari_kerja || []);
      if (set.has(v)) set.delete(v);
      else set.add(v);
      return { ...s, hari_kerja: Array.from(set) };
    });
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      nama: row.nama || "",
      jam_masuk: row.jam_masuk || "08:00",
      jam_pulang: row.jam_pulang || "17:00",
      toleransi_menit: row.toleransi_menit ?? 15,
      hari_kerja: Array.isArray(row.hari_kerja) && row.hari_kerja.length ? [...row.hari_kerja] : [...DEFAULT_HARI_KERJA],
    });
    setOpen(true);
  }
  async function del(r) {
    if (!confirm(`Hapus jadwal ${r.nama}?`)) return;
    try { await api.delete(`/master/jadwal/${r.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  }

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="page-jadwal">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Administrasi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Jadwal Kerja
          </h1>
          <p className="text-slate-500 text-sm mt-1">Kelola jadwal & toleransi keterlambatan.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty()); setOpen(true); }}
          className="bg-teal-700 hover:bg-teal-800 gap-2" data-testid="jadwal-add-btn">
          <Plus size={14} /> Tambah Jadwal
        </Button>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nama</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Jam Masuk</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Jam Pulang</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Toleransi</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Hari Kerja</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <Clock size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada jadwal kerja</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`jadwal-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell className="font-semibold text-slate-900">{r.nama}</TableCell>
                    <TableCell className="font-mono-clock text-slate-800">{r.jam_masuk}</TableCell>
                    <TableCell className="font-mono-clock text-slate-800">{r.jam_pulang}</TableCell>
                    <TableCell className="font-mono-clock text-xs text-slate-700">{r.toleransi_menit} menit</TableCell>
                    <TableCell className="text-xs text-slate-700" data-testid={`jadwal-hari-${r.id}`}>{formatHariKerja(r.hari_kerja)}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => openEdit(r)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800 hover:text-teal-900" data-testid={`jadwal-edit-${r.id}`}>
                        <Pencil size={13} /> Edit
                      </button>
                      {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="jadwal-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Jadwal" : "Tambah Jadwal"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Nama Jadwal</Label><Input className="mt-1.5" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="jadwal-input-nama" placeholder="WFO Reguler" /></div>
            <div><Label>Jam Masuk</Label><Input type="time" className="mt-1.5" value={form.jam_masuk} onChange={(e) => setForm({ ...form, jam_masuk: e.target.value })} data-testid="jadwal-input-masuk" /></div>
            <div><Label>Jam Pulang</Label><Input type="time" className="mt-1.5" value={form.jam_pulang} onChange={(e) => setForm({ ...form, jam_pulang: e.target.value })} data-testid="jadwal-input-pulang" /></div>
            <div className="sm:col-span-2"><Label>Toleransi Keterlambatan (menit)</Label><Input type="number" min={0} className="mt-1.5" value={form.toleransi_menit} onChange={(e) => setForm({ ...form, toleransi_menit: e.target.value })} data-testid="jadwal-input-toleransi" /></div>
            <div className="sm:col-span-2">
              <Label className="flex items-center justify-between">
                <span>Hari Kerja Efektif</span>
                <span className="text-[10px] font-normal text-slate-400">Default: Senin – Jumat</span>
              </Label>
              <div className="mt-2 grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="jadwal-hari-kerja-group">
                {HARI_LIST.map((h) => {
                  const active = (form.hari_kerja || []).includes(h.v);
                  return (
                    <label
                      key={h.v}
                      className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-xs font-semibold cursor-pointer select-none transition-colors ${active ? "border-teal-500 bg-teal-50 text-teal-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                      data-testid={`jadwal-hari-${h.v}`}
                    >
                      <input
                        type="checkbox"
                        className="accent-teal-700"
                        checked={active}
                        onChange={() => toggleHari(h.v)}
                        aria-label={h.label}
                      />
                      {h.short}
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Status <b>Alpha</b> & pemotongan kuota <b>Cuti</b> hanya berlaku pada hari kerja efektif (Sabtu/Minggu & Hari Libur Nasional otomatis di-skip).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={save} data-testid="jadwal-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
